/**
 * ShareLanding - Landing page for shared achievement cards
 *
 * Displays a shared ranking card and encourages sign-ups.
 * Features:
 * - Share card display with ranking info
 * - CTA to view full rankings
 * - Sign up invitation
 * - Expiration handling (30 days)
 */
import { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useAuthStore } from '../stores/authStore'
import shareService from '../services/shareService'
import LoadingSpinner from '../components/LoadingSpinner'
import {
  Trophy,
  Clock,
  Calendar,
  Target,
  TrendingUp,
  Share2,
  ArrowRight,
  AlertCircle,
  ExternalLink
} from 'lucide-react'
import { createLogger } from '../utils/logger'

const logger = createLogger('ShareLanding')

const ShareLanding = () => {
  const { shareId } = useParams()
  const { user } = useAuthStore()
  const [shareData, setShareData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  // Strategy display info
  const strategyInfo = {
    SAT_MATH: { name: 'SAT Math', icon: '🧮', color: 'from-blue-500 to-indigo-600' },
    SAT_MATH_MINI: { name: 'SAT Math Mini', icon: '⚡', color: 'from-yellow-500 to-orange-600' },
    SAT_READING_WRITING: { name: 'SAT R&W', icon: '📖', color: 'from-green-500 to-teal-600' },
    SAT_READING_WRITING_MINI: {
      name: 'SAT R&W Mini',
      icon: '✏️',
      color: 'from-purple-500 to-pink-600'
    }
  }

  useEffect(() => {
    const fetchShareData = async () => {
      try {
        setLoading(true)
        setError(null)
        const data = await shareService.getShareCard(shareId)
        setShareData(data)
      } catch (err) {
        logger.error('Error fetching share data:', err)
        if (err.status === 404) {
          setError('notFound')
        } else if (err.status === 410) {
          setError('expired')
        } else {
          setError('error')
        }
      } finally {
        setLoading(false)
      }
    }

    if (shareId) {
      fetchShareData()
    }
  }, [shareId])

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (error === 'notFound' || error === 'expired') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 p-4">
        <div className="text-center max-w-md">
          <AlertCircle className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">
            {error === 'expired' ? 'Link Expired' : 'Share Not Found'}
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            {error === 'expired'
              ? 'This share link has expired. Share links are valid for 30 days.'
              : 'This share link is invalid or has been removed.'}
          </p>
          <Link to="/rankings" className="btn-primary inline-flex items-center gap-2">
            View Current Rankings
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    )
  }

  if (error || !shareData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 p-4">
        <div className="text-center max-w-md">
          <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">
            Error Loading Share
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            Something went wrong. Please try again later.
          </p>
          <Link to="/rankings" className="btn-primary inline-flex items-center gap-2">
            View Rankings
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    )
  }

  const { cardType, strategyType, data } = shareData
  const strategy = strategyInfo[strategyType] || strategyInfo.SAT_MATH

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className={`bg-gradient-to-br ${strategy.color} text-white`}>
        <div className="max-w-2xl mx-auto px-4 py-12 text-center">
          <div className="text-4xl mb-4">{strategy.icon}</div>
          <h1 className="text-3xl font-bold mb-2">{data.publicName}'s Achievement</h1>
          <p className="text-white/80">{strategy.name} Challenge Result</p>
        </div>
      </div>

      {/* Share Card */}
      <div className="max-w-lg mx-auto px-4 -mt-8">
        <div className="card shadow-xl">
          <div className="card-body p-6">
            {/* Card Type: Ranking Summary */}
            {cardType === 'ranking_summary' && (
              <RankingSummaryCard data={data} strategy={strategy} />
            )}

            {/* Card Type: Single Challenge */}
            {cardType === 'challenge' && <ChallengeCard data={data} strategy={strategy} />}

            {/* Card Type: Level Up */}
            {cardType === 'level_up' && <LevelUpCard data={data} strategy={strategy} />}

            {/* Default: Achievement */}
            {!['ranking_summary', 'challenge', 'level_up'].includes(cardType) && (
              <AchievementCard data={data} strategy={strategy} />
            )}
          </div>
        </div>

        {/* CTAs */}
        <div className="mt-8 space-y-4 text-center pb-12">
          <Link
            to={`/leaderboard?highlight=${encodeURIComponent(data.publicName || '')}&strategy=${strategyType || ''}`}
            className="btn-primary inline-flex items-center gap-2 px-6 py-3"
            onClick={() => shareService.trackClick(shareId)}
          >
            <Trophy className="w-5 h-5" />
            View Full Rankings
            <ArrowRight className="w-4 h-4" />
          </Link>

          {!user && (
            <div className="mt-6">
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                Want to track your own progress?
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Link
                  to="/signup"
                  className="btn-secondary"
                  onClick={() => shareService.trackClick(shareId)}
                >
                  Request Access
                </Link>
                <Link
                  to="/login"
                  className="btn-ghost"
                  onClick={() => shareService.trackClick(shareId)}
                >
                  Log In
                </Link>
              </div>
            </div>
          )}

          {user && shareData.data?.publicName !== user.publicName && (
            <div className="mt-6">
              <Link
                to={`/u/${shareData.data?.publicName}`}
                className="text-primary-600 dark:text-primary-400 hover:underline inline-flex items-center gap-1"
                onClick={() => shareService.trackClick(shareId)}
              >
                View {shareData.data?.publicName}'s Full Profile
                <ExternalLink className="w-4 h-4" />
              </Link>
            </div>
          )}

          {/* Branding */}
          <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">Powered by</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-gray-700 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
              onClick={() => shareService.trackClick(shareId)}
            >
              <span className="text-xl font-bold bg-gradient-to-r from-primary-600 to-purple-600 bg-clip-text text-transparent">
                Deaveris
              </span>
            </Link>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
              SAT prep with adaptive learning
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

/**
 * Ranking Summary Card
 */
const RankingSummaryCard = ({ data, strategy }) => (
  <div className="text-center">
    <div className="w-16 h-16 rounded-full bg-gradient-to-br from-primary-400 to-purple-500 flex items-center justify-center text-white text-2xl font-bold mx-auto mb-4">
      {data.publicName?.[0]?.toUpperCase() || '?'}
    </div>
    <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-1">{data.publicName}</h2>
    <p className="text-gray-600 dark:text-gray-400 mb-6">{strategy.name} Rankings</p>

    <div className="bg-gradient-to-r from-yellow-100 to-amber-100 dark:from-yellow-900/30 dark:to-amber-900/30 rounded-xl p-6 mb-4">
      <div className="flex items-center justify-center gap-2 mb-2">
        <Trophy className="w-8 h-8 text-yellow-600" />
        <span className="text-4xl font-bold text-gray-900 dark:text-gray-100">#{data.rank}</span>
      </div>
      <p className="text-gray-700 dark:text-gray-300">
        out of {data.totalParticipants?.toLocaleString()} players
      </p>
      <p className="text-green-600 dark:text-green-400 font-medium mt-2">
        Top {(100 - (data.percentile || 0)).toFixed(1)}%
      </p>
    </div>

    <div className="grid grid-cols-2 gap-4 text-sm">
      <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
        <Target className="w-5 h-5 mx-auto mb-1 text-gray-400" />
        <div className="font-semibold text-gray-900 dark:text-gray-100">
          {data.score?.toFixed(1) || '--'}%
        </div>
        <div className="text-gray-500 dark:text-gray-400">Best Score</div>
      </div>
      <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
        <TrendingUp className="w-5 h-5 mx-auto mb-1 text-gray-400" />
        <div className="font-semibold text-gray-900 dark:text-gray-100">
          Level {data.level || 1}
        </div>
        <div className="text-gray-500 dark:text-gray-400">Current Level</div>
      </div>
    </div>
  </div>
)

/**
 * Challenge Card
 */
const ChallengeCard = ({ data, strategy }) => (
  <div className="text-center">
    <div className="text-4xl mb-4">{strategy.icon}</div>
    <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-1">Challenge Complete!</h2>
    <p className="text-gray-600 dark:text-gray-400 mb-6">
      {data.publicName} · {strategy.name}
    </p>

    <div className="bg-green-50 dark:bg-green-900/20 rounded-xl p-6 mb-4">
      <div className="text-4xl font-bold text-green-600 dark:text-green-400 mb-2">
        {data.score?.toFixed(1) || '--'}%
      </div>
      <p className="text-gray-600 dark:text-gray-400">Score</p>
    </div>

    <div className="grid grid-cols-2 gap-4 text-sm">
      <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
        <Clock className="w-5 h-5 mx-auto mb-1 text-gray-400" />
        <div className="font-semibold text-gray-900 dark:text-gray-100">
          {formatTime(data.timeSpent)}
        </div>
        <div className="text-gray-500 dark:text-gray-400">Time</div>
      </div>
      <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
        <Trophy className="w-5 h-5 mx-auto mb-1 text-gray-400" />
        <div className="font-semibold text-gray-900 dark:text-gray-100">#{data.rank || '--'}</div>
        <div className="text-gray-500 dark:text-gray-400">Rank</div>
      </div>
    </div>
  </div>
)

/**
 * Level Up Card
 */
const LevelUpCard = ({ data, strategy }) => (
  <div className="text-center">
    <div className="text-6xl mb-4">🎉</div>
    <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-1">Level Up!</h2>
    <p className="text-gray-600 dark:text-gray-400 mb-6">
      {data.publicName} reached a new level in {strategy.name}
    </p>

    <div className="bg-gradient-to-r from-purple-100 to-pink-100 dark:from-purple-900/30 dark:to-pink-900/30 rounded-xl p-6">
      <div className="text-5xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
        Level {data.level}
      </div>
      <p className="text-gray-600 dark:text-gray-400 mt-2">
        {strategy.icon} {strategy.name}
      </p>
    </div>
  </div>
)

/**
 * Achievement Card (default)
 */
const AchievementCard = ({ data, strategy }) => (
  <div className="text-center">
    <div className="text-4xl mb-4">{data.icon || '🏆'}</div>
    <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-1">
      {data.title || 'Achievement Unlocked!'}
    </h2>
    <p className="text-gray-600 dark:text-gray-400 mb-4">
      {data.publicName} · {strategy.name}
    </p>
    {data.description && <p className="text-gray-700 dark:text-gray-300">{data.description}</p>}
  </div>
)

/**
 * Format time in mm:ss
 */
const formatTime = (seconds) => {
  if (!seconds) return '--:--'
  const mins = Math.floor(seconds / 60)
  const secs = seconds % 60
  return `${mins}:${secs.toString().padStart(2, '0')}`
}

export default ShareLanding
